import { Subject } from 'rxjs';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { TranslationService } from '@sdbn/common';
import { ConfigurationService, HttpService } from '@sdbn/appclient';
import { IdGeneratorService } from 'src/common/services/id-generator.service';
import { environment } from 'src/environments/environment';
import { ArticleType, ArticleTypeCalculationPrecission } from 'src/erp-articles/article-types/article-type.model';
import { ArticleTypeService } from 'src/erp-articles/article-types/article-type.service';
import { Article, BasicArticle } from 'src/erp-articles/catalog/catalog.model';
import { RetailPrice, RetailPriceCalculationValue } from 'src/erp-articles/erp-common/erp-common.model';
import { ProjectPosition, ProjectPositonCalculationMode } from 'src/erp-projects/project/project.model';
import { PriceContainer } from './../../../../../erp-articles/erp-common/erp-common.model';

@Component({
  selector: 'app-price-editor',
  templateUrl: './price-editor.component.html',
  styleUrls: ['./price-editor.component.scss']
})
export class PriceEditorComponent implements OnInit, OnChanges {

  hideVatPrices = true;
  priceScale = 2;

  initialized = false;

  _referenceDate: Date;

  _amount: number;

  _projectPosition: ProjectPosition;

  _article: Article;

  articleType: ArticleType;

  newPrice = false;

  @Input()
  formGroup: FormGroup;

  calculationResults = new Map<number, number>();

  processingFormChange = false;

  basicArticle: BasicArticle = null;

  result = 'open';

  resultingPriceNet: number = null;

  availableCostsPriceSources = new Array<string>();
  calculationModeArticleBased = ProjectPositonCalculationMode.ARTICLE_BASED;
  calculationModeManualAmount = ProjectPositonCalculationMode.MANUAL_AMOUNT;
  calculationModeManual = ProjectPositonCalculationMode.MANUAL;
  calculationModeFixed = ProjectPositonCalculationMode.FIXED;

  @Input() set article(article: Article) {
    this._article = article;
    if (this._article != null) {
      this.articleType = this.articleTypeService.getById(this._article.articleTypeId).completeArticleType;
      this.loadBasicArticle().subscribe(() => {
        this.initialized = true;
        this.initializeForm();
        if (this.projectPosition != null && this._referenceDate != null) {
          this.determineValidRetailPriceAndUpdatePriceFields(this.projectPosition.calculationMode);
        }
      });
    }
  }

  get article() {
    return this._article;
  }

  get isPriceFixedVisible(): boolean {
    return this.projectPosition.calculationMode === this.calculationModeFixed;
  }

  @Input() set projectPosition(projectPosition: ProjectPosition) {
    this._projectPosition = projectPosition;
    if (this.projectPosition.price == null) {
      this.newPrice = true;
      this.projectPosition.price = new RetailPrice({});
    }
    this.projectPosition.calculationMode = this.projectPosition.calculationMode != null ? this.projectPosition.calculationMode : this.calculationModeArticleBased;
  }

  get projectPosition() {
    return this._projectPosition;
  }

  @Input() set referenceDate(referenceDate: Date) {
    this._referenceDate = referenceDate;
    if (this.article != null && this.basicArticle != null && this.projectPosition != null && this._referenceDate != null) {
      this.determineValidRetailPriceAndUpdatePriceFields(this.projectPosition.calculationMode);
    }
  }

  @Input() set amount(amount: number) {
    this._amount = amount;
    this.calculatePriceAndSum();
  }

  get amount() {
    return this._amount;
  }

  constructor(
    private articleTypeService: ArticleTypeService,
    public cps: ConfigurationService,
    public translationService: TranslationService,
    private idGenerator: IdGeneratorService,
    private http: HttpService
  ) {
    this.hideVatPrices = this.cps.getClientParamValue('erp.hide-vat-prices', 'false') === 'true';
    this.priceScale = this.cps.getClientParamValue('erp.use-three-digit-prices', 'false') === 'true' ? 3 : 2;
  }

  ngOnInit(): void {
    this.formGroup.addControl('calculationMode', new FormControl());
    this.formGroup.addControl('costsPriceSource', new FormControl('', [Validators.required]));
    this.formGroup.addControl('price_net', new FormControl(null, []));
    this.formGroup.addControl('tax_rate', new FormControl(null, []));
    this.formGroup.addControl('price_vat', new FormControl(null, []));
    this.formGroup.addControl('price_fixed', new FormControl(null, []));
    this.formGroup.patchValue({
      calculationMode: this.projectPosition.calculationMode,
      costsPriceSource: this.projectPosition.costsPriceSource
    });
  }

  ngOnChanges() {

  }

  private initializeForm() {
    // get EK sources
    this.article?.priceContainer?.costsPrices.forEach(costsPrice => {
      if (costsPrice.source != null && this.availableCostsPriceSources.indexOf(costsPrice.source) < 0) {
        this.availableCostsPriceSources.push(costsPrice.source);
      }
    });
    if (this.articleType.calculationRules != null) {
      for (const rule of this.articleType.calculationRules) {
        this.formGroup.addControl('rule_count_' + rule.id, new FormControl(null, []));
        this.formGroup.addControl('rule_faktor_' + rule.id, new FormControl(null, []));
        if (rule.precission === ArticleTypeCalculationPrecission.ATTRIBUTE) {
          this.formGroup.controls['rule_count_' + rule.id].disable();
        }
      }
    }

    this.determineValidRetailPriceAndUpdatePriceFields(this.projectPosition.calculationMode);
    this.handleCalculationModeSelection(this.projectPosition.calculationMode);
    this.applyFormListeners();
    this.formGroup.markAllAsTouched();
  }

  private applyFormListeners() {
    // Handle calculation Mode selection
    this.formGroup.controls.calculationMode.valueChanges.subscribe(v => {
      this.handleCalculationModeSelection(v);
    });

    // Handle value changes
    this.formGroup.valueChanges.subscribe(formValues => {
      if (this.processingFormChange) {
        return;
      }

      this.processingFormChange = true;
      const taxRate = this.formGroup.controls['tax_rate'].value;
      const priceNet = this.formGroup.controls['price_net'].value;
      const priceVat = this.formGroup.controls['price_vat'].value;
      const priceFixed = this.formGroup.controls['price_fixed'].value;

      this.projectPosition.price.taxRate = taxRate != null && taxRate !== '' ? parseFloat(taxRate) : null;
      this.projectPosition.price.priceNet = priceNet != null && priceNet !== '' ? parseFloat(priceNet) : null;
      this.projectPosition.price.priceVat = priceVat != null && priceVat !== '' ? parseFloat(priceVat) : null;
      this.projectPosition.priceFixed = priceFixed != null && priceFixed !== '' ? parseFloat(priceFixed) : null;
      this.projectPosition.costsPriceSource = formValues.costsPriceSource;

      // calculate resulting price
      this.calculatePriceAndSum();

      this.processingFormChange = false;

    });
  }

  private handleCalculationModeSelection(calculationMode: ProjectPositonCalculationMode) {
    this.projectPosition.calculationMode = calculationMode;
    if (calculationMode === this.calculationModeArticleBased) {
      this.formGroup.controls.price_net.disable();
      this.formGroup.controls.price_vat.disable();
      this.formGroup.controls.tax_rate.disable();
      this.formGroup.controls.price_fixed.disable();
      for (const rule of this.articleType.calculationRules) {
        this.formGroup.controls['rule_count_' + rule.id].disable();
        this.formGroup.controls['rule_faktor_' + rule.id].disable();

      }
      this.determineValidRetailPriceAndUpdatePriceFields(calculationMode);
    } else if (calculationMode === this.calculationModeManualAmount) {
      this.formGroup.controls.price_net.disable();
      this.formGroup.controls.price_vat.disable();
      this.formGroup.controls.tax_rate.disable();
      this.formGroup.controls.price_fixed.disable();
      for (const rule of this.articleType.calculationRules) {
        this.formGroup.controls['rule_count_' + rule.id].enable();
        this.formGroup.controls['rule_faktor_' + rule.id].disable();
      }
      this.determineValidRetailPriceAndUpdatePriceFields(calculationMode);
    } else if (calculationMode === this.calculationModeManual) {
      this.formGroup.controls.price_net.enable();
      this.formGroup.controls.price_vat.enable();
      this.formGroup.controls.tax_rate.enable();
      this.formGroup.controls.price_fixed.disable();
      for (const rule of this.articleType.calculationRules) {
        this.formGroup.controls['rule_count_' + rule.id].enable();
        this.formGroup.controls['rule_faktor_' + rule.id].enable();
      }
    } else if (calculationMode === this.calculationModeFixed) {
      this.formGroup.controls.price_net.disable();
      this.formGroup.controls.price_vat.disable();
      this.formGroup.controls.tax_rate.disable();
      this.formGroup.controls.price_fixed.enable();
      this.formGroup.controls.price_fixed.setValue(this.projectPosition.priceFixed);
      for (const rule of this.articleType.calculationRules) {
        this.formGroup.controls['rule_count_' + rule.id].disable();
        this.formGroup.controls['rule_faktor_' + rule.id].disable();
      }
    }
  }

  private determineValidRetailPriceAndUpdatePriceFields(calculationMode: ProjectPositonCalculationMode, initial = false) {
    let priceToUse;
    let validArticleRetailPrice = this.getValidRetailPriceForArticle();
    if (this.newPrice || calculationMode === ProjectPositonCalculationMode.ARTICLE_BASED || calculationMode === ProjectPositonCalculationMode.MANUAL_AMOUNT) {
      priceToUse = validArticleRetailPrice;
    } else {
      priceToUse = this.projectPosition.price;
    }
    this.formGroup.controls.tax_rate.setValue(priceToUse != null ? priceToUse.taxRate : 19);
    this.formGroup.controls.price_net.setValue(priceToUse != null ? priceToUse.priceNet : null);
    this.formGroup.controls.price_vat.setValue(priceToUse != null ? priceToUse.priceVat : null);
    for (const rule of this.articleType.calculationRules) {
      const valueFromArticlePrice = this.getValueByRuleId(validArticleRetailPrice, rule.id);
      const valueFromPositionPrice = this.getValueByRuleId(this.projectPosition.price, rule.id);
      if (calculationMode === ProjectPositonCalculationMode.ARTICLE_BASED) {
        let count = valueFromArticlePrice != null ? valueFromArticlePrice.count : null;
        if (this.article.retailPricesFromBasicArticle === true && valueFromArticlePrice != null) { // count itself must be taken from article attribute
          const attributeValue = this.article.getValueByAttributeId(rule.attributeId);
          if (!Number.isNaN(attributeValue)) {
            count = Number.parseFloat(attributeValue);
          }
        }
        this.formGroup.controls['rule_count_' + rule.id].setValue(count);
        this.formGroup.controls['rule_faktor_' + rule.id].setValue(valueFromArticlePrice != null ? valueFromArticlePrice.value : null);
      } else if (calculationMode === ProjectPositonCalculationMode.MANUAL_AMOUNT) {
        this.formGroup.controls['rule_count_' + rule.id].setValue(valueFromPositionPrice != null ? valueFromPositionPrice.count : null);
        this.formGroup.controls['rule_faktor_' + rule.id].setValue(valueFromArticlePrice != null ? valueFromArticlePrice.value : null);
      } else {
        this.formGroup.controls['rule_count_' + rule.id].setValue(valueFromPositionPrice != null ? valueFromPositionPrice.count : null);
        this.formGroup.controls['rule_faktor_' + rule.id].setValue(valueFromPositionPrice != null ? valueFromPositionPrice.value : null);
      }
    }

    this.calculatePriceAndSum();
  }

  private getValueByRuleId(retailPrice: RetailPrice, id: number): RetailPriceCalculationValue {
    if (retailPrice != null && retailPrice.calculationValues != null) {
      for (const value of retailPrice.calculationValues) {
        if (value.ruleAttributeId === id) {
          return value;
        }
      }
    }
    return null;
  }

  private getOrCreateValue(retailPrice: RetailPrice, id: number): RetailPriceCalculationValue {
    for (const value of retailPrice.calculationValues) {
      if (value.ruleAttributeId === id) {
        return value;
      }
    }
    const value = new RetailPriceCalculationValue({ id: this.idGenerator.getNextId() });
    value.ruleAttributeId = id;
    retailPrice.calculationValues.push(value);
    return value;
  }

  private loadBasicArticle(): Subject<void> {
    const subject = new Subject<void>();
    this.http.postWithContext<BasicArticle>(environment.erpArticleEndpoint + '/loadBasicArticle', { articleId: this.article.id }).subscribe(basicArticle => {
      if (basicArticle != null) {
        this.basicArticle = new BasicArticle(basicArticle);
      }
      subject.next();
      subject.complete();
    }, error => {
      console.log('error is', error);
      subject.next();
      subject.complete();
    });
    return subject;
  }

  private calculatePriceAndSum() {
    if (this.projectPosition != null && this.article != null && this.articleType != null) {
      for (const calculationRule of this.articleType.calculationRules) {

        const value = this.getOrCreateValue(this.projectPosition.price, calculationRule.id);

        const ruleCountControl = this.formGroup.controls['rule_count_' + calculationRule.id];
        const ruleFaktorControl = this.formGroup.controls['rule_faktor_' + calculationRule.id];
        let parsedCount = 0;
        if (calculationRule.precission === ArticleTypeCalculationPrecission.ATTRIBUTE && this.projectPosition.calculationMode == this.calculationModeArticleBased) {
          parsedCount = parseFloat(this.article.getValueByAttributeId(calculationRule.attributeId));
        } else {
          parsedCount = parsedCount = ruleCountControl != null ? parseFloat(ruleCountControl.value) : null;
        }
        const parsedValue = ruleFaktorControl != null ? parseFloat(ruleFaktorControl.value) : null;
        value.count = isNaN(parsedCount) ? null : parsedCount;
        value.value = isNaN(parsedValue) ? null : parsedValue;
        value.calculate();
        this.calculationResults.set(calculationRule.id, value.total);
      }
      let totalPrice = this.projectPosition.price.priceNet;

      if (totalPrice != null) {
        this.calculationResults.forEach(cV => {
          if (cV != null) {
            totalPrice = totalPrice + cV;
          }
        });
      }
      if (this.amount != null) {
        this.resultingPriceNet = totalPrice * this.amount;
      }
    }

  }

  private getValidRetailPriceForArticle(): RetailPrice {
    if (this.article == null) {
      return null;
    }
    if (this.article.retailPricesFromBasicArticle === true) {
      return this.getValidRetailPriceFromPriceContainer(this.basicArticle.priceContainer);
    } else {
      return this.getValidRetailPriceFromPriceContainer(this.article.priceContainer);
    }
  }

  private getValidRetailPriceFromPriceContainer(priceContainer: PriceContainer): RetailPrice {
    const now = this._referenceDate;
    for (const retailPrice of priceContainer.retailPrices) {
      let validFrom = DateTime.fromISO(retailPrice.validPeriod.from);
      let validUntil = retailPrice.validPeriod.until != null ? DateTime.fromISO(retailPrice.validPeriod.until) : null;
      validFrom = retailPrice.validPeriod.from;
      validUntil = retailPrice.validPeriod.until;
      if (validFrom <= now
        && (validUntil == null || validUntil > now)) {
        return retailPrice;
      }
    }
    return null;
  }
}

